import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import BadgeRow from "@/components/BadgeRow";
import TitleTag from "@/components/TitleTag";
import { FlagBadge } from "@/lib/flags";

const RANGES = [
  { key: "daily",     label: "Daily",            icon: "fa-sun" },
  { key: "weekly",    label: "Weekly",           icon: "fa-calendar-week" },
  { key: "monthly",   label: "Monthly",          icon: "fa-calendar-days" },
  { key: "yearly",    label: "Yearly",           icon: "fa-calendar" },
  { key: "exp",       label: "EXP Rank",         icon: "fa-trophy" },
  { key: "challenge", label: "PnL",              icon: "fa-bolt" },
];

function formatCountdown(targetIso) {
  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  const now = Date.now();
  let diff = Math.max(0, Math.floor((target - now) / 1000));
  const d = Math.floor(diff / 86400); diff -= d * 86400;
  const h = Math.floor(diff / 3600);  diff -= h * 3600;
  const m = Math.floor(diff / 60);    diff -= m * 60;
  const s = diff;
  if (d > 0) return `${d}d ${String(h).padStart(2,"0")}h ${String(m).padStart(2,"0")}m`;
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
}

export default function LeaderboardModal({ onClose, initialRange = "daily" }) {
  const [range, setRange] = useState(initialRange);
  const [rows, setRows] = useState([]);
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // V2.5.2 fix: clear stale rows immediately so we never render PnL columns
    // against EXP-shaped data (was the cross-tab crash root cause).
    setRows([]);
    setCycle(null);
    setLoading(true);
    (async () => {
      try {
        let data;
        if (range === "exp") {
          const r = await api.get("/achievements/leaderboard", { params: { limit: 100 } });
          data = { rows: r.data || [], cycle: null };
        } else if (range === "challenge") {
          // V2.9.6 — "Challenge Pts" tab renamed to "PnL" and now sources all-time total PnL.
          const r = await api.get("/leaderboard/total-pnl", { params: { limit: 100 } });
          data = { rows: r.data?.rows || [], cycle: null };
        } else {
          const r = await api.get("/leaderboard", { params: { range } });
          data = { rows: r.data?.rows || [], cycle: r.data?.cycle || null };
        }
        if (!cancelled) { setRows(data.rows); setCycle(data.cycle); }
      } catch {
        if (!cancelled) { setRows([]); setCycle(null); }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [range]);

  // 1s tick for countdown
  useEffect(() => {
    if (range === "exp" || range === "challenge") return; // no countdown for these tabs
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [range]);

  const countdown = cycle?.end ? formatCountdown(cycle.end) : null;
  const cycleLabel = cycle?.start && cycle?.end
    ? `${new Date(cycle.start).toLocaleString([], {month:"short", day:"numeric", hour:"numeric", minute:"2-digit"})}  →  ${new Date(cycle.end).toLocaleString([], {month:"short", day:"numeric", hour:"numeric", minute:"2-digit"})}  EST`
    : "All time";

  const isCycleRange = range !== "exp" && range !== "challenge";

  return (
    <div data-testid="leaderboard-modal" className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4">
      <div className="bg-[#151a24] border border-slate-800 rounded-2xl max-w-3xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4 border-b border-slate-800 pb-3">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2 font-mono">
              <i className="fa-solid fa-trophy text-amber-400"/> Trader Leaderboard
            </h2>
            <p className="text-xs text-slate-400 font-mono">PnL by cycle · <span className="text-amber-300">EXP Rank</span> for achievements · <span className="text-purple-300">Challenge Pts</span> for daily replay scores</p>
          </div>
          <button data-testid="lb-close" onClick={onClose} className="text-slate-500 hover:text-white"><i className="fa-solid fa-xmark text-xl"/></button>
        </div>

        <div className="flex flex-wrap gap-2 mb-3 font-mono">
          {RANGES.map(r => (
            <button key={r.key} data-testid={`lb-range-${r.key}`} onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 rounded-md text-[11px] font-bold transition-all ${range === r.key
                ? "bg-amber-500 text-slate-900"
                : "bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800"}`}>
              <i className={`fa-solid ${r.icon} mr-1.5`}/> {r.label}
            </button>
          ))}
        </div>

        {/* Cycle window + countdown */}
        {cycle && (
          <div data-testid="lb-cycle" className="mb-4 px-3 py-2 bg-slate-900/60 border border-slate-800 rounded-lg flex items-center justify-between text-[11px] font-mono">
            <div>
              <span className="text-slate-500">Cycle: </span>
              <span className="text-slate-300">{cycleLabel}</span>
            </div>
            {countdown && isCycleRange && (
              <div data-testid="lb-countdown" className="flex items-center gap-2">
                <i className="fa-solid fa-stopwatch text-amber-400"/>
                <span className="text-slate-500">Resets in</span>
                <span className="text-amber-300 font-bold tabular-nums">{countdown}</span>
              </div>
            )}
            <span className="hidden">{tick}</span>
          </div>
        )}

        {loading ? (
          <div className="text-center py-10 text-slate-500 text-sm"><i className="fa-solid fa-spinner fa-spin mr-2"/> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-sm">
            {range === "exp" ? "No EXP earned yet. Be the first to unlock achievements!"
            : range === "challenge" ? "No challenge points scored yet. Hit the Daily Challenge to start climbing!"
            : "No trades in this cycle yet. Be the first!"}
          </div>
        ) : range === "exp" ? (
          <ExpTable rows={rows} onClose={onClose}/>
        ) : range === "challenge" ? (
          <ChallengeTable rows={rows} onClose={onClose}/>
        ) : (
          <PnlTable rows={rows} onClose={onClose}/>
        )}
      </div>
    </div>
  );
}

// ---------------- Table sub-components ----------------

function Rank({ i }) {
  if (i < 3) {
    const cls = i === 0 ? "text-amber-400" : i === 1 ? "text-slate-300" : "text-orange-400";
    return <span className={cls}>★ {i+1}</span>;
  }
  return i + 1;
}

function Avatar({ name, avatar }) {
  return avatar
    ? <img src={avatar} alt="" className="w-5 h-5 rounded-full object-cover border border-slate-700"/>
    : <div className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[9px] text-slate-500">{(name||"?").charAt(0).toUpperCase()}</div>;
}

function PnlTable({ rows, onClose }) {
  // V2.9.6 — was the cycle leaderboard's body; rebuilt to rank by Pts instead of PnL.
  // Columns: # | Trader | Pts | Trades | Win Rate | Sessions
  return (
    <table className="w-full text-[11px] font-mono" data-testid="lb-pnl-table">
      <thead className="text-slate-500 border-b border-slate-800">
        <tr>
          <th className="text-left py-2 px-2">#</th>
          <th className="text-left py-2 px-2">Trader</th>
          <th className="text-right py-2 px-2">Pts</th>
          <th className="text-right py-2 px-2">Trades</th>
          <th className="text-right py-2 px-2">Win Rate</th>
          <th className="text-right py-2 px-2">Sessions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.user_id} className={`border-b border-slate-800/60 ${r.is_me ? "bg-blue-500/10" : ""}`}>
            <td className="py-2 px-2 text-slate-400"><Rank i={i}/></td>
            <td className="py-2 px-2 text-white font-bold">
              <Link to={`/u/${r.user_id}`} onClick={onClose} className="flex items-center gap-2 hover:text-amber-300 transition-colors" data-testid={`lb-user-${r.user_id}`}>
                <Avatar name={r.name} avatar={r.avatar}/>
                <FlagBadge code={r.country} size="sm"/>
                <span style={r.name_color ? { color: r.name_color } : undefined}>{r.name}</span>
                <TitleTag userId={r.user_id} size="sm"/>
                <BadgeRow userId={r.user_id} size="sm"/>
                <span title={r.online ? "Online" : "Offline"} className={`w-1.5 h-1.5 rounded-full ${r.online ? "bg-emerald-400" : "bg-slate-600"}`}/>
                {r.is_me && <span className="text-blue-400 text-[9px] ml-1">(you)</span>}
              </Link>
            </td>
            <td className="py-2 px-2 text-right text-purple-300 font-black tabular-nums">{(r.points ?? 0).toLocaleString()}</td>
            <td className="py-2 px-2 text-right text-slate-300">{r.trades ?? 0}</td>
            <td className="py-2 px-2 text-right text-amber-400">{(r.win_rate ?? 0).toFixed(0)}%</td>
            <td className="py-2 px-2 text-right text-slate-300 tabular-nums">{r.sessions ?? 0}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ExpTable({ rows, onClose }) {
  return (
    <table className="w-full text-[11px] font-mono" data-testid="lb-exp-table">
      <thead className="text-slate-500 border-b border-slate-800">
        <tr>
          <th className="text-left py-2 px-2">#</th>
          <th className="text-left py-2 px-2">Trader</th>
          <th className="text-right py-2 px-2">EXP</th>
          <th className="text-right py-2 px-2">Achievements</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.user_id} className="border-b border-slate-800/60">
            <td className="py-2 px-2 text-slate-400"><Rank i={i}/></td>
            <td className="py-2 px-2 text-white font-bold">
              <Link to={`/u/${r.user_id}`} onClick={onClose} className="flex items-center gap-2 hover:text-amber-300 transition-colors" data-testid={`lb-exp-user-${r.user_id}`}>
                <Avatar name={r.name} avatar={r.avatar}/>
                <FlagBadge code={r.country} size="sm"/>
                <span style={r.name_color ? { color: r.name_color } : undefined}>{r.name}</span>
                <TitleTag userId={r.user_id} size="sm"/>
                <BadgeRow userId={r.user_id} size="sm"/>
              </Link>
            </td>
            <td className="py-2 px-2 text-right text-amber-300 font-black tabular-nums">{(r.exp ?? 0).toLocaleString()}</td>
            <td className="py-2 px-2 text-right text-purple-300 font-bold tabular-nums">{r.unlocked_count ?? 0}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ChallengeTable({ rows, onClose }) {
  // V2.9.6 — was Challenge Pts; now ranks all-time total PnL.
  // Columns: # | Trader | PnL | Trade amounts
  return (
    <table className="w-full text-[11px] font-mono" data-testid="lb-challenge-table">
      <thead className="text-slate-500 border-b border-slate-800">
        <tr>
          <th className="text-left py-2 px-2">#</th>
          <th className="text-left py-2 px-2">Trader</th>
          <th className="text-right py-2 px-2">PnL</th>
          <th className="text-right py-2 px-2">Trade amounts</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.user_id} className={`border-b border-slate-800/60 ${r.is_me ? "bg-blue-500/10" : ""}`}>
            <td className="py-2 px-2 text-slate-400"><Rank i={i}/></td>
            <td className="py-2 px-2 text-white font-bold">
              <Link to={`/u/${r.user_id}`} onClick={onClose} className="flex items-center gap-2 hover:text-amber-300 transition-colors" data-testid={`lb-challenge-user-${r.user_id}`}>
                <Avatar name={r.name} avatar={r.avatar}/>
                <FlagBadge code={r.country} size="sm"/>
                <span style={r.name_color ? { color: r.name_color } : undefined}>{r.name}</span>
                <TitleTag userId={r.user_id} size="sm"/>
                <BadgeRow userId={r.user_id} size="sm"/>
                {r.is_me && <span className="text-blue-400 text-[9px] ml-1">(you)</span>}
              </Link>
            </td>
            <td className={`py-2 px-2 text-right font-black tabular-nums ${(r.total_pnl ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>${(r.total_pnl ?? 0).toFixed(2)}</td>
            <td className="py-2 px-2 text-right text-slate-300 tabular-nums">{r.trades ?? 0}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
